import type { MaybeRef } from '@/hooks/usePolicyTypeSelection'
import type { Group, GroupType } from '@/types'
import ApiService from '@/service/api.service.js'
import {
  keepPreviousData,
  useInfiniteQuery,
  useQueries,
  useQuery,
} from '@tanstack/vue-query'
import { computed, unref } from 'vue'
import { getUsersByGroupId } from '@/service/v3/endpoints/groups'
import { getDepartments, getGroups } from './groupsApi'
import {
  QUERY_KEY_DEPARTMENT_LEVEL,
  QUERY_KEY_DEPARTMENTS_SEARCH,
  QUERY_KEY_GROUP,
  QUERY_KEY_GROUP_MEMBER_COUNT,
  QUERY_KEY_GROUPS_INFINITE,
  QUERY_KEY_GROUPS_PAGINATED,
} from './groupsKeys'
import type { GroupListRow, UseGroupsPaginatedQueryParams } from './groupsTypes'
import type { ApiGroupType } from '../user/userTypes'

/** The API calls custom groups `'default'` */
export const toGroupType = (groupType: ApiGroupType): GroupType =>
  groupType === 'default' ? 'custom' : groupType

export const useGroupQuery = (
  groupId: MaybeRef<number | string | undefined>
) => {
  return useQuery({
    queryKey: computed(() => [QUERY_KEY_GROUP, unref(groupId)]),
    queryFn: async () => {
      const response = await ApiService.getGroupById(unref(groupId))
      if (response.error) {
        throw new Error(response.statusText ?? 'Failed to fetch group')
      }
      return response.data as Group
    },
    enabled: computed(() => unref(groupId) != null),
  })
}

export type DepartmentNode = {
  id: number
  name: string
  parentId: number | null
  hasChildren: boolean
  isArchived: boolean
}

export type DepartmentLevel = {
  nodes: DepartmentNode[]
  totalCount: number
}

export type DepartmentsResponse = Awaited<ReturnType<typeof getDepartments>>

export const toDepartmentLevel = (
  response: DepartmentsResponse
): DepartmentLevel => ({
  nodes: response.data.map((department) => ({
    id: Number(department.id),
    name: department.attributes.name,
    parentId: department.attributes.parent_id,
    hasChildren: (department.relationships?.children?.data?.length ?? 0) > 0,
    isArchived: department.attributes.hide_from_reports,
  })),
  totalCount: response.meta.pagination.total_count,
})

/** Fixed, so the browse queries and the subtree walk address the same cache
 *  entries. Paging grows the number of pages, never the page size: a bigger
 *  size is a different key, and would refetch the rows already on screen. */
export const DEPARTMENT_LEVEL_PAGE_SIZE = 50

export type DepartmentLevelParams = {
  companyNameKey: string | undefined
  /** `null` is the root level */
  parentId: number | null
  pageSize: number
  page?: number
  /** Part of the key: the active and archived roots differ only by this. */
  hideFromReports?: boolean
}

/** Shared with the tree's subtree walk, which hands the same options to
 *  `fetchQuery`, so browsing and selecting share cache entries. */
export const departmentLevelQuery = ({
  companyNameKey,
  parentId,
  pageSize,
  page = 1,
  hideFromReports,
}: DepartmentLevelParams) => ({
  queryKey: [
    QUERY_KEY_DEPARTMENT_LEVEL,
    companyNameKey,
    parentId,
    pageSize,
    page,
    hideFromReports,
  ],
  queryFn: () =>
    getDepartments({
      companyNameKey: companyNameKey!,
      parentId,
      pageSize,
      page,
      hideFromReports,
    }),
})

export type DepartmentLevelPageRequest = {
  parentId: number | null
  page: number
  hideFromReports?: boolean
}

export type UseDepartmentLevelsQueriesParams = {
  companyNameKey: MaybeRef<string | undefined>
  /** One entry per loaded page: a level showing three pages has three entries */
  pages: MaybeRef<DepartmentLevelPageRequest[]>
  enabled?: MaybeRef<boolean>
}

/** `useQueries` because the number of open pages varies at runtime, and `setup`
 *  cannot establish subscriptions by looping.
 *
 *  No `placeholderData`: observers here are matched by array index, so carrying
 *  the previous one's data over renders another level's rows under the wrong
 *  parent whenever collapsing or expanding shifts the indices. Each page keeps
 *  its own cache entry, so there is nothing to bridge. */
export const useDepartmentLevelsQueries = ({
  companyNameKey,
  pages,
  enabled = true,
}: UseDepartmentLevelsQueriesParams) =>
  useQueries({
    queries: computed(() =>
      unref(pages).map(({ parentId, page, hideFromReports }) => ({
        ...departmentLevelQuery({
          companyNameKey: unref(companyNameKey),
          parentId,
          pageSize: DEPARTMENT_LEVEL_PAGE_SIZE,
          page,
          hideFromReports,
        }),
        enabled: !!unref(companyNameKey) && unref(enabled),
        select: toDepartmentLevel,
      }))
    ),
  })

export type UseDepartmentSearchInfiniteQueryParams = {
  companyNameKey: MaybeRef<string | undefined>
  search: MaybeRef<string>
  pageSize?: MaybeRef<number>
  enabled?: MaybeRef<boolean>
}

/** Matches from anywhere in the tree, as a flat list */
export const useDepartmentSearchInfiniteQuery = ({
  companyNameKey,
  search,
  pageSize = 25,
  enabled = true,
}: UseDepartmentSearchInfiniteQueryParams) => {
  const query = useInfiniteQuery({
    queryKey: computed(() => [
      QUERY_KEY_DEPARTMENTS_SEARCH,
      unref(companyNameKey),
      unref(search),
      unref(pageSize),
    ]),
    queryFn: ({ pageParam }) =>
      getDepartments({
        companyNameKey: unref(companyNameKey)!,
        parentId: null,
        search: unref(search),
        page: pageParam,
        pageSize: unref(pageSize),
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage) return undefined
      const { current_page, total_pages } = lastPage.meta.pagination
      return current_page < total_pages ? current_page + 1 : undefined
    },
    enabled: computed(
      () => !!unref(companyNameKey) && !!unref(search) && unref(enabled)
    ),
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => toDepartmentLevel(page).nodes),
    }),
  })

  const items = computed(() => query.data.value?.pages.flat() ?? [])

  return { ...query, items }
}

export const useGroupMemberCountQuery = (
  groupId: MaybeRef<number | string | undefined>
) => {
  return useQuery({
    queryKey: computed(() => [QUERY_KEY_GROUP_MEMBER_COUNT, unref(groupId)]),
    queryFn: async () => {
      const response = await getUsersByGroupId({
        groupId: String(unref(groupId)),
        pagination: { 'page[size]': 1, 'page[number]': 1 },
      })
      return response.meta.pagination.total_count as number
    },
    enabled: computed(() => unref(groupId) != null),
  })
}

export type UseGroupsInfiniteQueryParams = {
  companyNameKey: MaybeRef<string | undefined>
  groupType: MaybeRef<GroupType>
  search?: MaybeRef<string>
  pageSize?: MaybeRef<number>
  /**
   * Position-specific filter for selected positions.
   * - undefined: no filter applied
   * - false: primary positions only
   * - true: additional positions only
   */
  additional?: MaybeRef<boolean | undefined>
  enabled?: MaybeRef<boolean>
}

export const useGroupsInfiniteQuery = ({
  companyNameKey,
  groupType,
  search = '',
  pageSize = 25,
  additional,
  enabled = true,
}: UseGroupsInfiniteQueryParams) => {
  const queryKey = computed(() => [
    QUERY_KEY_GROUPS_INFINITE,
    unref(groupType),
    unref(companyNameKey),
    unref(search),
    unref(pageSize),
    unref(additional),
  ])

  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      getGroups({
        companyNameKey: unref(companyNameKey)!,
        groupType: unref(groupType),
        search: unref(search),
        page: pageParam,
        pageSize: unref(pageSize),
        additional: unref(additional),
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage) return undefined
      const { current_page, total_pages } = lastPage.meta.pagination
      return current_page < total_pages ? current_page + 1 : undefined
    },
    enabled: computed(() => !!unref(companyNameKey) && unref(enabled)),
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) =>
        page?.data.map(
          (item) =>
            ({
              id: Number(item.id),
              name: item.attributes.name,
              group_type: toGroupType(item.attributes.group_type),
            }) as Group
        )
      ),
    }),
  })

  const items = computed(() => query.data.value?.pages.flat() ?? [])

  return {
    ...query,
    items,
  }
}

export const useGroupsPaginatedQuery = <C extends boolean>({
  companyNameKey,
  groupType,
  pageNumber,
  pageSize,
  sort,
  search,
  includeUserCount,
}: UseGroupsPaginatedQueryParams<C>) =>
  useQuery({
    queryKey: computed((): (string | number | boolean | undefined)[] => [
      QUERY_KEY_GROUPS_PAGINATED,
      unref(groupType),
      unref(pageNumber),
      unref(pageSize),
      unref(sort),
      unref(search),
      unref(includeUserCount),
      unref(companyNameKey),
    ]),
    queryFn: () => {
      const nameKey = unref(companyNameKey)
      if (!nameKey) throw new Error('Missing company name key')

      return getGroups({
        companyNameKey: nameKey,
        groupType: unref(groupType),
        page: unref(pageNumber),
        pageSize: unref(pageSize),
        sort: unref(sort),
        search: unref(search),
        includeUserCount: unref(includeUserCount),
      })
    },
    enabled: computed(() => !!unref(companyNameKey)),
    placeholderData: keepPreviousData,
    select: (response) => ({
      groups: response.data.map(
        (group) =>
          ({
            id: group.id,
            name: group.attributes.name,
            userCount:
              'user_count' in group.attributes
                ? group.attributes.user_count
                : undefined,
          }) as GroupListRow<C>
      ),
      totalCount: response.meta.pagination.total_count,
      totalPages: response.meta.pagination.total_pages,
    }),
  })
